/**
 *
 * AboutThePerson
 *
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import Grid from '@material-ui/core/Grid';
import { Link } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import Avatar from '@material-ui/core/Avatar';
import SaveAsFavorite from 'components/SaveAsFavorite';
import { getImageFile } from 'utils/stringUtils';
import { makeSelectSavedResources } from 'containers/Resources/selectors';
import { getSavedResources, saveResource } from 'containers/Resources/actions';
import { isResourceSaved } from 'containers/Resources/utils';
import TwitterIcon from 'components/SocialIcons/twitter.png';
import FacebookIcon from 'components/SocialIcons/facebook.svg';
import InstaIcon from 'components/SocialIcons/instagram.png';
import LinkedInIcon from 'components/SocialIcons/linkedin.svg';
// import AuthorRelatedTopics from 'components/AuthorRelatedTopics';
import VisibilitySensor from 'react-visibility-sensor';
import { useScrollData } from 'scroll-data-hook';
import Mixpanel from 'utils/mixpanelService';
import { getFormattedType } from 'components/ResourceItemSelector/utils';

const styles = theme => ({
  root: {
    marginBottom: 29,
    marginTop: 20,
  },
  heading: {
    marginBottom: 54,
  },
  avatar: {
    width: 192,
    height: 192,
    [theme.breakpoints.down('960')]: {
      width: 125,
      height: 125,
    },
  },
  name: {
    textDecoration: 'none',
    marginRight: 15,
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    marginBottom: 17,
    color: theme.palette.text.disabled,
  },
  websiteWrapper: {
    marginBottom: 11,
  },
  website: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    ...theme.typography.body2,
  },
  detailsWrapper: {
    marginBottom: 15,
  },
  icon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  descriptionWrapper: {
    '& span': {
      fontSize: 17,
      lineHeight: 1.5,
    },
  },
});

const AboutThePerson = props => {
  const { data, heading, classes, pageData } = props;
  const savedResources = useSelector(makeSelectSavedResources());
  const dispatch = useDispatch();
  const { scrolling } = useScrollData();

  useEffect(() => {
    dispatch(getSavedResources());
  }, [data]);

  const savePerson = item => {
    const {
      sys: { id: resourceID },
      fields: { slug, name },
    } = item;
    const saved = isResourceSaved(savedResources, item);

    let nodeID;
    if (saved)
      [{ nodeID }] = savedResources.filter(el => el.resourceID === resourceID);

    const payloadData = {
      nodeID,
      resource: {
        type: 'People',
        resourceID,
        slug,
        name,
      },
      save: !saved,
    };
    dispatch(saveResource(payloadData));
  };

  const visibilityChange = isVisible => {
    const {
      fields,
      fields: { blog, name, title },
      sys,
      sys: { id },
    } = pageData[0];

    let type = getFormattedType(sys.contentType.sys.id);
    if (blog) type = 'blog';
    else if (_get(fields, 'type') === 'News') type = 'news';
    if (isVisible && scrolling) {
      Mixpanel.track('Resource Page - ScrollTo', {
        path: window.location.pathname,
        section: 'about-person-panel',
        type,
        name: name || title,
        entryId: id,
      });
    }
  };

  if (_isEmpty(data) || _isEmpty(_get(data, '0.fields'))) return null;
  // const topics = this.getAllTopics(data);
  return (
    <div className={classes.root}>
      <VisibilitySensor
        onChange={visibilityChange}
        scrollCheck
        scrollDelay={500}
      >
        <>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              color="textPrimary"
              className={classes.heading}
            >
              {heading}
            </Typography>
          </Grid>
          {data.map(item => {
            const imageFile = getImageFile(item.fields);
            if (_isEmpty(_get(item, 'fields'))) return null;
            const {
              name = '',
              description,
              title,
              website,
              slug,
              twitter,
              facebook,
              instagram,
              linkedIn,
              reviewStatus,
            } = item.fields;
            const websiteUrl =
              website &&
              website.match(
                /^(?:https?:)?(?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n]+)/im,
              );
            const saved = isResourceSaved(savedResources, item);
            const isAcceptedItem = reviewStatus === 'Accepted';
            return (
              <Grid
                item
                container
                xs={12}
                spacing={2}
                className={classes.detailsWrapper}
                key={slug}
              >
                <Grid item xs={12} sm={4}>
                  <Avatar
                    src={imageFile}
                    className={classes.avatar}
                    alt="About The Person"
                  />
                </Grid>
                <Grid item container xs={12} sm={8}>
                  <Grid item container direction="row" xs={12}>
                    {isAcceptedItem ? (
                      <Link to={`/people/${slug}`} className={classes.name}>
                        <Typography variant="h6" color="textPrimary">
                          {name}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography
                        variant="h6"
                        color="textPrimary"
                        className={classes.name}
                      >
                        {name}
                      </Typography>
                    )}
                    <SaveAsFavorite
                      id={item.sys.id}
                      saved={saved}
                      save={() => savePerson(item)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      className={classes.title}
                      gutterBottom
                    >
                      {title}
                    </Typography>
                  </Grid>
                  <div className={classes.descriptionWrapper}>
                    <RichTextReactRenderer
                      data={description}
                      type="name"
                      name={name}
                      contentType="person"
                    />
                  </div>
                  {website && (
                    <Grid item xs={12} className={classes.websiteWrapper}>
                      <a
                        href={websiteUrl[0]}
                        target="_blank"
                        rel="noopener"
                        className={classes.website}
                      >
                        {websiteUrl[0]}
                      </a>
                    </Grid>
                  )}
                  <Grid item container direction="row" xs={12}>
                    {twitter && (
                      <a href={twitter} target="_blank" rel="noopener">
                        <img
                          src={TwitterIcon}
                          alt="Twitter"
                          className={classes.icon}
                        />
                      </a>
                    )}
                    {facebook && (
                      <a href={facebook} target="_blank" rel="noopener">
                        <img
                          src={FacebookIcon}
                          alt="Facebook"
                          className={classes.icon}
                        />
                      </a>
                    )}
                    {instagram && (
                      <a href={instagram} target="_blank" rel="noopener">
                        <img
                          src={InstaIcon}
                          alt="Insta"
                          className={classes.icon}
                        />
                      </a>
                    )}
                    {linkedIn && (
                      <a href={linkedIn} target="_blank" rel="noopener">
                        <img
                          src={LinkedInIcon}
                          alt="Linkedin"
                          className={classes.icon}
                        />
                      </a>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
          {/* <AuthorRelatedTopics topics={topics} /> */}
        </>
      </VisibilitySensor>
    </div>
  );
};

AboutThePerson.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.array,
  heading: PropTypes.string,
  pageData: PropTypes.object,
};

export default withStyles(styles)(AboutThePerson);
